/**
 * Workspace Slice
 *
 * Manages the teacher workspace state including:
 * - Active app selection
 * - Recent apps history
 * - Favorite apps
 * - Saved presentations library
 */

import type { StateCreator } from 'zustand';
import type { AppStore } from '../types';

/**
 * Maximum number of recent apps to track
 */
const MAX_RECENT_APPS = 5;

/**
 * Extract Google Slides presentation ID from URL
 */
function extractGoogleSlidesId(url: string): string | null {
  const match = url.match(/\/presentation\/d\/([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
}

/**
 * Presentation type
 */
export type SavedPresentationType = 'google-slides' | 'pdf';

/**
 * Saved presentation data
 */
export interface SavedPresentation {
  /** Unique identifier */
  id: string;
  /** User-given name (e.g., "Chapter 5 - Fractions") */
  name: string;
  /** Presentation type */
  type: SavedPresentationType;
  /** Full Google Slides URL (only for google-slides type) */
  googleSlidesUrl?: string;
  /** Extracted presentation ID for embedding (only for google-slides type) */
  presentationId: string;
  /** PDF download URL from Firebase Storage (only for pdf type) */
  pdfUrl?: string;
  /** PDF storage path for deletion (only for pdf type) */
  pdfStoragePath?: string;
  /** Total number of slides/pages (optional, for progress display) */
  totalSlides: number | null;
  /** ISO date string when created */
  createdAt: string;
  /** ISO date string when last updated */
  updatedAt: string;
}

/**
 * Generate a unique ID for a presentation
 */
function generatePresentationId(): string {
  return `pres_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Workspace state
 */
export interface WorkspaceState {
  /** Currently active app ID (null if on workspace homepage) */
  activeAppId: string | null;
  /** Recently used app IDs (ordered by recency, most recent first) */
  recentAppIds: string[];
  /** User's favorite/pinned app IDs */
  favoriteAppIds: string[];
  /** Workspace view mode for the app grid */
  viewMode: 'grid' | 'list';
  /** Saved presentations library */
  savedPresentations: SavedPresentation[];
}

/**
 * Workspace actions
 */
export interface WorkspaceActions {
  /** Set the currently active app */
  setActiveApp: (appId: string | null) => void;
  /** Add an app to the recent apps list */
  addToRecent: (appId: string) => void;
  /** Toggle an app as favorite */
  toggleFavorite: (appId: string) => void;
  /** Add an app to favorites */
  addFavorite: (appId: string) => void;
  /** Remove an app from favorites */
  removeFavorite: (appId: string) => void;
  /** Set the workspace view mode */
  setViewMode: (mode: 'grid' | 'list') => void;
  /** Clear all recent apps */
  clearRecentApps: () => void;
  /** Clear all favorites */
  clearFavorites: () => void;
  /** Reset workspace to initial state */
  resetWorkspace: () => void;
  /** Add a new Google Slides presentation (returns the new presentation or null if invalid URL) */
  addSavedPresentation: (name: string, url: string, totalSlides?: number) => SavedPresentation | null;
  /** Add a new PDF presentation (returns the new presentation) */
  addPDFPresentation: (name: string, pdfUrl: string, pdfStoragePath: string, totalPages?: number) => SavedPresentation;
  /** Update an existing saved presentation */
  updateSavedPresentation: (id: string, updates: Partial<Pick<SavedPresentation, 'name' | 'totalSlides'>>) => void;
  /** Remove a saved presentation (also handles PDF deletion via callback) */
  removeSavedPresentation: (id: string) => SavedPresentation | undefined;
  /** Get a saved presentation by ID */
  getSavedPresentation: (id: string) => SavedPresentation | undefined;
  /** Set all saved presentations (used for sync from Firestore) */
  setSavedPresentations: (presentations: SavedPresentation[]) => void;
}

export type WorkspaceSlice = WorkspaceState & WorkspaceActions;

/**
 * Initial workspace state
 */
const initialWorkspaceState: WorkspaceState = {
  activeAppId: null,
  recentAppIds: [],
  favoriteAppIds: [],
  viewMode: 'grid',
  savedPresentations: [],
};

/**
 * Create workspace slice
 */
export const createWorkspaceSlice: StateCreator<AppStore, [], [], WorkspaceSlice> = (set) => ({
  // Initial state
  ...initialWorkspaceState,

  // Actions
  setActiveApp: (appId) => {
    set((state) => {
      // If setting an active app, also add it to recents
      if (appId) {
        const recentAppIds = [appId, ...state.recentAppIds.filter((id) => id !== appId)].slice(
          0,
          MAX_RECENT_APPS
        );
        return { activeAppId: appId, recentAppIds };
      }
      return { activeAppId: null };
    });
  },

  addToRecent: (appId) => {
    set((state) => {
      // Move to front if already exists, or add new
      const recentAppIds = [appId, ...state.recentAppIds.filter((id) => id !== appId)].slice(
        0,
        MAX_RECENT_APPS
      );
      return { recentAppIds };
    });
  },

  toggleFavorite: (appId) => {
    set((state) => {
      const isFavorite = state.favoriteAppIds.includes(appId);
      const favoriteAppIds = isFavorite
        ? state.favoriteAppIds.filter((id) => id !== appId)
        : [...state.favoriteAppIds, appId];
      return { favoriteAppIds };
    });
  },

  addFavorite: (appId) => {
    set((state) => {
      if (state.favoriteAppIds.includes(appId)) {
        return state; // Already a favorite
      }
      return { favoriteAppIds: [...state.favoriteAppIds, appId] };
    });
  },

  removeFavorite: (appId) => {
    set((state) => ({
      favoriteAppIds: state.favoriteAppIds.filter((id) => id !== appId),
    }));
  },

  setViewMode: (mode) => {
    set({ viewMode: mode });
  },

  clearRecentApps: () => {
    set({ recentAppIds: [] });
  },

  clearFavorites: () => {
    set({ favoriteAppIds: [] });
  },

  resetWorkspace: () => {
    set(initialWorkspaceState);
  },

  addSavedPresentation: (name: string, url: string, totalSlides?: number) => {
    const presentationId = extractGoogleSlidesId(url);
    if (!presentationId) {
      return null; // Invalid URL
    }

    const now = new Date().toISOString();
    const newPresentation: SavedPresentation = {
      id: generatePresentationId(),
      name: name.trim(),
      type: 'google-slides',
      googleSlidesUrl: url,
      presentationId,
      totalSlides: totalSlides ?? null,
      createdAt: now,
      updatedAt: now,
    };

    set((state) => ({
      savedPresentations: [...state.savedPresentations, newPresentation],
    }));

    return newPresentation;
  },

  addPDFPresentation: (name: string, pdfUrl: string, pdfStoragePath: string, totalPages?: number) => {
    const now = new Date().toISOString();
    const id = generatePresentationId();
    const newPresentation: SavedPresentation = {
      id,
      name: name.trim(),
      type: 'pdf',
      presentationId: id, // Use same ID for consistency
      pdfUrl,
      pdfStoragePath,
      totalSlides: totalPages ?? null,
      createdAt: now,
      updatedAt: now,
    };

    set((state) => ({
      savedPresentations: [...state.savedPresentations, newPresentation],
    }));

    return newPresentation;
  },

  updateSavedPresentation: (id: string, updates: Partial<Pick<SavedPresentation, 'name' | 'totalSlides'>>) => {
    set((state) => ({
      savedPresentations: state.savedPresentations.map((pres) =>
        pres.id === id
          ? {
              ...pres,
              ...updates,
              name: updates.name !== undefined ? updates.name.trim() : pres.name,
              updatedAt: new Date().toISOString(),
            }
          : pres
      ),
    }));
  },

  removeSavedPresentation: (id: string) => {
    let removed: SavedPresentation | undefined;
    set((state) => {
      removed = state.savedPresentations.find((pres) => pres.id === id);
      return {
        savedPresentations: state.savedPresentations.filter((pres) => pres.id !== id),
      };
    });
    return removed;
  },

  getSavedPresentation: (_id: string) => {
    // Note: This returns current state, not reactive
    // For reactive access, use the selector directly
    return undefined; // Will be implemented via selector
  },

  setSavedPresentations: (presentations: SavedPresentation[]) => {
    set({ savedPresentations: presentations });
  },
});
